import {
  LoadingOverlay, Stack, Group, Text,
  Button, useMantineTheme,
  ActionIcon,
  Image,
  Divider
} from '@mantine/core';
import { useCallback } from 'react';
import { IconDownload } from '@tabler/icons-react';

import { ProposalExportFileType, ProposalExportType } from '../../types/apiTypes';
import ModalContent from '../ModalContent';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import Excel from '../../assets/excel-logo.svg';
import Word from '../../assets/word-logo.svg';

interface Props {
  onClose: () => void
  proposalUid: string
}

interface ExportType {
  exportType: ProposalExportType
  fileType: ProposalExportFileType
  label: string
  description: string
  icon: React.ReactElement
}

const EXPORT_TYPES: ExportType[] = [
  {
    exportType: ProposalExportType.OUTLINE,
    fileType: ProposalExportFileType.DOCX,
    label: 'Outline (.docx)',
    description: 'Proposal formatted as requirements paired with their responses',
    icon: <Image src={Word} alt='Word Logo' h={48} w={48} />
  },
  {
    exportType: ProposalExportType.NARRATIVE,
    fileType: ProposalExportFileType.DOCX,
    label: 'Narrative (.docx) ',
    description: 'Proposal with sections built from responses to each requirement',
    icon: <Image src={Word} alt='Word Logo' h={48} w={48} />
  },
  {
    exportType: ProposalExportType.SPREADSHEET,
    fileType: ProposalExportFileType.CSV,
    label: 'Spreadsheet (.csv)',
    description: 'All requirements and responses',
    icon: <Image src={Excel} alt='Excel Logo' h={48} w={48} />
  }
];

const DownloadProposal: React.FC<Props> = (props: Props) => {
  const { onClose, proposalUid } = props;
  const { colors, other: colorVars } = useMantineTheme();

  const exportProposalMutation = useEnsisMutation(
    '/app/proposal-exports',
    {
      contentType: 'application/json',
      requestType: 'post',
      showSuccessMessage: false,
      onSuccess: (data) => {
        if (data?.url != null) {
          window.open(data?.url, '_blank', 'noreferrer');
        }
      }
    }
  );

  const handleExportProposal = useCallback(({ exportType, fileType }: ExportType) => {
    exportProposalMutation.mutate({
      proposal_uid: proposalUid,
      export_type: exportType,
      file_type: fileType
    });
  }, [exportProposalMutation.mutate, proposalUid]);

  const handleDownloadAll = useCallback(() => {
    exportProposalMutation.mutate({
      proposal_uid: proposalUid,
      file_type: ProposalExportFileType.ZIP
    });
  }, [exportProposalMutation.mutate, proposalUid]);

  return (
    <ModalContent
      title='Download Proposal'
      subtitle='Download files to review, collaborate, or continue editing in Ensis for Word or Excel.'
      primaryButton={{
        label: 'Done',
        onClick: onClose
      }}
      onClose={onClose}
    >
      <LoadingOverlay visible={exportProposalMutation.isPending} zIndex={1000} />
      <Stack p='16px 0px'>
        <Group justify='flex-end' mb={16}>
          <Button
            disabled={exportProposalMutation.isPending}
            onClick={handleDownloadAll}
            variant='outline'
            leftSection={<IconDownload color={colors.lightPurple[4]} />}
          >
            Download All
          </Button>
        </Group>
        {
          EXPORT_TYPES.map((exportType, index) => (
            <>
              <Group key={exportType.exportType} justify='space-between' pl={24}>
                <Group gap={16}>
                  {exportType.icon}
                  <Stack gap={0}>
                    <Text fz='sm' fw={600}>{exportType.label}</Text>
                    <Text fz='sm' c={colorVars.secondaryTextColor}>{exportType.description}</Text>
                  </Stack>
                </Group>
                <ActionIcon
                  onClick={() => { handleExportProposal(exportType); }}
                  variant='subtle'
                >
                  <IconDownload color={colorVars.primaryIconColor} />
                </ActionIcon>
              </Group>
              {index !== EXPORT_TYPES.length - 1 && <Divider />}
            </>
          ))
        }
      </Stack>
    </ModalContent>
  );
};

export default DownloadProposal;
